import { TripAccessorialsSection } from '../../dispatch/public/TripAccessorialsSection';
import { DriverCaptureSection } from '../../dispatch/public/DriverCaptureSection';
import { TripCashSection } from '../../dispatch/public/TripCashSection';
import { TripTimelineSection } from '../../dispatch/public/TripTimelineSection';
import { TripExpensesPanel } from '../features/trip-expenses/components/TripExpensesPanel';
import { TripMessagesPanel } from '../features/trip-messages/components/TripMessagesPanel';
import { useTripPermissions } from '../hooks/use-trip-permissions';
import type { TripStop } from '../policies/trip-stops.policy';
import type { TripTab } from '../trips.paths';
import type { TripPageData } from '../trips.api';
import { SectionNotAvailable } from './SectionNotAvailable';
import { TripEventsTab } from './TripEventsTab';
import { TripHistoryTab } from './TripHistoryTab';
import { TripOverviewTab } from './TripOverviewTab';

interface TripTabBodyProps {
  tab: TripTab;
  trip: TripPageData;
  stops: TripStop[];
  /** Counts the page's re-reads (a minute's poll, a fresh fix, an action); sections that show derived data read again when it moves. */
  readCount: number;
  onChanged: () => void;
}

const PLACEHOLDER_NAMES: Partial<Record<TripTab, string>> = { alerts: 'Alerts', exceptions: 'Exceptions', documents: 'Documents' };

/** The two tabs that act for the driver and so need the crew's driver. */
function DriverTabBody({ tab, trip, onChanged }: TripTabBodyProps) {
  const permissions = useTripPermissions();
  if (!trip.driver && tab !== 'cash') return null;
  const driver = trip.driver ? { id: trip.driver.id, label: trip.driver.name } : null;

  if (tab === 'record' && driver) return <DriverCaptureSection tripId={trip.id} driver={driver} />;
  if (tab === 'messages') return <TripMessagesPanel tripId={trip.id} canSend={permissions.canCommunicate} />;
  return (
    <TripCashSection
      tripId={trip.id}
      tripNumber={trip.number}
      tripStatus={trip.status}
      driver={driver}
      canTopUp={permissions.canTopUp}
      canViewCash={permissions.canViewCash}
      canRecordCash={permissions.canRecordCash}
      onChanged={onChanged}
    />
  );
}

/** The body of one tab. Each section loads and reports its own states, so one failing never takes the page down with it. */
export function TripTabBody(props: TripTabBodyProps) {
  const { tab, trip, onChanged } = props;
  const placeholder = PLACEHOLDER_NAMES[tab];
  if (placeholder) return <SectionNotAvailable name={placeholder} />;

  switch (tab) {
    case 'overview':
      return <TripOverviewTab trip={trip} stops={props.stops} onFreshFix={onChanged} />;
    case 'timeline':
      return <TripTimelineSection tripId={trip.id} tripNumber={trip.number} refreshKey={props.readCount} />;
    case 'events':
      return <TripEventsTab tripId={trip.id} />;
    case 'expenses':
      return <TripExpensesPanel tripId={trip.id} />;
    case 'accessorials':
      return <TripAccessorialsSection tripId={trip.id} />;
    case 'history':
      return <TripHistoryTab tripId={trip.id} />;
    default:
      return <DriverTabBody {...props} />;
  }
}
